import { RWSService } from "@rws-framework/client";

const DEFAULT_PRIMARY_HEX = "#55bab7";
const RUNTIME_THEME_STYLE_ID = "app-runtime-theme";
// Keep in sync with frontend/panel-admin/src/styles/variables.css
const DEFAULT_THEME = {
    fontFamily: "Roboto, sans-serif",
    primaryColor: DEFAULT_PRIMARY_HEX,
    primaryRgb: "85, 186, 183",
    primaryText: "#000",
    secondaryColor: "#e81e75",

    sidebarBg: "var(--neutral-70)",
    sidebarFg: "var(--neutral-30)",
    sidebarFgMuted: "var(--neutral-40)",
    sidebarDividerColor: "var(--neutral-30)",
    sidebarHoverBg: "var(--neutral-a8)",
    sidebarActiveBg: "var(--primary-a8)",
    sidebarActiveBorder: "var(--primary-40)",
    sidebarActiveFg: "var(--neutral-10)",
    sidebarSearchBg: "var(--neutral-a36)",
    sidebarSearchFocusBg: "var(--neutral-80)",
    sidebarSearchFocusOutline: "var(--neutral-30)",
    sidebarActiveRgb: "85, 186, 183",
    sidebarHoverRgb: "27, 28, 30",

    logoLargeSize: "100%",
    logoSmallSize: "100%",
    logoSplashSize: "256px",
} as const;

interface ThemeBundleShape {
    highlights: {
        primary?: string;
        secondary?: string;
        primary_text?: string;
        font?: string;
    };
    header: {
        background_color?: string;
        background_color_active?: string;
        background_color_hover?: string;
        icon_color?: string;
        icon_color_active?: string;
    };
}

interface LabelBundleShape {
    title?: string;
    large_size?: string;
    small_size?: string;
}

export interface RuntimeThemeConfig {
    themeBundle: ThemeBundleShape;
    labelBundle: LabelBundleShape;
}

class ThemeService extends RWSService {
    static _IN_CLIENT: boolean = true;

    applyRuntimeTheme(runtimeConfig: RuntimeThemeConfig, document = window.document) {
        return ThemeService.applyRuntimeTheme(runtimeConfig, document);
    }

    public static applyRuntimeTheme(runtimeConfig: RuntimeThemeConfig, document = window.document) {
        const theme = runtimeConfig.themeBundle;
        const labels = runtimeConfig.labelBundle;
        const documentTitle = labels.title?.trim();

        if (documentTitle) document.title = `Admin Panel | ${documentTitle}`;

        const primaryColor = normalizeThemeColor(theme.highlights.primary, DEFAULT_PRIMARY_HEX);
        const primaryRgb = toRgbTuple(primaryColor) ?? DEFAULT_THEME.primaryRgb;
        const activeBackgroundRgb = normalizeColorTuple(
            theme.header.background_color_active,
            DEFAULT_THEME.sidebarActiveRgb,
        );
        const hoverBackgroundRgb = normalizeColorTuple(
            theme.header.background_color_hover,
            DEFAULT_THEME.sidebarHoverRgb,
        );
        const primaryText = normalizeThemeColor(theme.highlights.primary_text, getReadableTextColor(primaryColor));
        const sidebarBackground = normalizeThemeColor(theme.header.background_color, DEFAULT_THEME.sidebarBg);
        const sidebarForeground = normalizeThemeColor(theme.header.icon_color, DEFAULT_THEME.sidebarFg);

        const themeVariables: Record<string, string> = {
            "--app-font-family": normalizeFontFamily(theme.highlights.font),
            // `!important` is required because Fluent installs its own default via an adopted
            // stylesheet, which outranks plain `<style>`.
            "--body-font": "var(--app-font-family) !important",
            "--app-primary-color": primaryColor,
            "--app-primary-rgb": primaryRgb,
            "--app-primary-text": primaryText,
            "--app-secondary-color": normalizeThemeColor(theme.highlights.secondary, DEFAULT_THEME.secondaryColor),

            // sidebar
            "--app-sidebar-bg": sidebarBackground,
            "--app-sidebar-fg": sidebarForeground,
            "--app-sidebar-fg-muted": normalizeThemeColor(theme.header.icon_color, DEFAULT_THEME.sidebarFgMuted),
            "--app-sidebar-divider-color": normalizeThemeColor(
                theme.header.icon_color,
                DEFAULT_THEME.sidebarDividerColor,
            ),
            "--app-sidebar-hover-rgb": hoverBackgroundRgb,
            "--app-sidebar-hover-bg": `rgba(${hoverBackgroundRgb}, 0.52)`,
            "--app-sidebar-active-rgb": activeBackgroundRgb,
            "--app-sidebar-active-bg": `rgba(${activeBackgroundRgb}, 0.08)`,
            "--app-sidebar-active-bg-strong": `rgba(${activeBackgroundRgb}, 0.20)`,
            "--app-sidebar-active-border": `rgb(${activeBackgroundRgb})`,
            "--app-sidebar-active-fg": normalizeThemeColor(
                theme.header.icon_color_active,
                DEFAULT_THEME.sidebarActiveFg,
            ),
            "--app-sidebar-search-bg": `rgba(${hoverBackgroundRgb}, 0.36)`,
            "--app-sidebar-search-focus-bg": DEFAULT_THEME.sidebarSearchFocusBg,
            "--app-sidebar-search-focus-outline": normalizeThemeColor(
                theme.header.icon_color_active,
                DEFAULT_THEME.sidebarSearchFocusOutline,
            ),

            // logo sizing
            "--app-logo-large-size": normalizePercent(labels.large_size, DEFAULT_THEME.logoLargeSize),
            "--app-logo-small-size": normalizePercent(labels.small_size, DEFAULT_THEME.logoSmallSize),
        };

        const styleTag = getRuntimeThemeStyleTag(document);
        styleTag.textContent = `:root {\n${Object.entries(themeVariables)
            .map(([name, value]) => `    ${name}: ${value};`)
            .join("\n")}\n}`;
    }
}

export default ThemeService.getSingleton();
export { ThemeService as ThemeServiceInstance };

function getRuntimeThemeStyleTag(document: Document) {
    const existingTag = document.getElementById(RUNTIME_THEME_STYLE_ID);
    if (existingTag instanceof HTMLStyleElement) return existingTag;

    const styleTag = document.createElement("style");
    styleTag.id = RUNTIME_THEME_STYLE_ID;
    document.body.appendChild(styleTag);
    return styleTag;
}

/** check validity of color css value string */
function normalizeThemeColor(value: string | undefined, fallback: string) {
    if (!value) return fallback;

    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith("var(")) return trimmed;
    if (typeof CSS !== "undefined" && CSS.supports("color", trimmed)) return trimmed;

    return fallback;
}

/** adds default fallback fonts */
function normalizeFontFamily(value: string | undefined) {
    const trimmed = value?.trim();
    if (!trimmed) return DEFAULT_THEME.fontFamily;

    const hasRoboto = /(^|,|\s)roboto(,|\s|$)/i.test(trimmed);
    const hasSansSerif = /sans-serif/i.test(trimmed);

    if (hasRoboto && hasSansSerif) return trimmed;
    if (hasRoboto) return `${trimmed}, sans-serif`;
    if (hasSansSerif) return hasRoboto ? trimmed : `${trimmed}, Roboto`;

    return `${trimmed}, Roboto, sans-serif`;
}

/** accepts color in format "r, g, b", "rgb(r, g, b)", returns "r, g, b" */
function normalizeColorTuple(value: string | undefined, fallback: string) {
    const trimmed = value?.trim();
    if (!trimmed) return fallback;

    const rawTuple = /^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/.exec(trimmed);
    if (rawTuple) {
        const [, r, g, b] = rawTuple;
        if ([r, g, b].every((channel) => Number(channel) >= 0 && Number(channel) <= 255)) {
            return `${Number(r)}, ${Number(g)}, ${Number(b)}`;
        }
    }

    const rgbTuple = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(trimmed);
    if (rgbTuple) {
        const [, r, g, b] = rgbTuple;
        if ([r, g, b].every((channel) => Number(channel) >= 0 && Number(channel) <= 255)) {
            return `${Number(r)}, ${Number(g)}, ${Number(b)}`;
        }
    }

    return toRgbTuple(trimmed) ?? fallback;
}

function normalizePercent(value: string | undefined, fallback: string) {
    const numeric = Number.parseFloat(value || "");
    if (!Number.isFinite(numeric) || numeric <= 0) return fallback;

    return `${numeric}%`;
}

/** picks contrasting text color */
function getReadableTextColor(color: string) {
    const rgb = toRgb(color);

    if (!rgb) return DEFAULT_THEME.primaryText;

    const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return yiq >= 128 ? "#3B3C3E" : "#FFF";
}

/** converts hex color to "r, g, b" tuple */
function toRgbTuple(color: string) {
    const rgb = toRgb(color);
    if (!rgb) return null;

    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

function toRgb(color: string): { r: number; g: number; b: number } {
    const normalized = normalizeHex(color);

    if (!normalized) return null;

    return {
        r: Number.parseInt(normalized.slice(1, 3), 16),
        g: Number.parseInt(normalized.slice(3, 5), 16),
        b: Number.parseInt(normalized.slice(5, 7), 16),
    };
}

/** ensures hex is in "#RRGGBB" format */
function normalizeHex(color: string) {
    const trimmed = color.trim();
    const short = /^#?([\da-f]{3})$/i.exec(trimmed);
    if (short) {
        const [, value] = short;
        return `#${value
            .split("")
            .map((char) => `${char}${char}`)
            .join("")}`;
    }

    const full = /^#?([\da-f]{6})$/i.exec(trimmed);
    if (full) return `#${full[1]}`;

    return null;
}
